import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { entrepriseService } from '../services/entreprise.service';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Rapports.css';

interface Ligne { nom: string; nombre: number; montant: number; }
interface Rapport {
  periode: { debut: string; fin: string };
  resume: {
    nombreVentes: number;
    chiffreAffaires: number;
    panierMoyen: number;
    remisesTotales: number;
    parMode: { especes: number; mobileMoney: number; credit: number; virement: number };
  };
  parJour: { jour: string; montant: number; nombre: number }[];
  topProduits: { produit: string; quantite: number; montant: number }[];
  parVendeur: Ligne[];
  parBoutique: Ligne[];
}

const fmt = (n: number) => Math.round(Number(n || 0)).toLocaleString('fr-FR');
// Formatage sûr pour le PDF : séparateur = espace ASCII (jsPDF n'affiche pas
// l'espace insécable étroit U+202F produit par toLocaleString('fr-FR')).
const fmtPdf = (n: number) =>
  Math.round(Number(n || 0)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const jolieDate = (s: string) => new Date(s).toLocaleDateString('fr-FR');

type Preset = 'jour' | '7j' | 'mois' | 'moisDernier' | 'perso';

// Bornes de dates (YYYY-MM-DD) pour un préréglage.
function bornes(preset: Preset): { debut: string; fin: string } {
  const aujourdhui = new Date();
  if (preset === 'jour') return { debut: iso(aujourdhui), fin: iso(aujourdhui) };
  if (preset === '7j') {
    const d = new Date(aujourdhui);
    d.setDate(d.getDate() - 6);
    return { debut: iso(d), fin: iso(aujourdhui) };
  }
  if (preset === 'mois') {
    const d = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), 1);
    return { debut: iso(d), fin: iso(aujourdhui) };
  }
  if (preset === 'moisDernier') {
    const debut = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth() - 1, 1);
    const fin = new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), 0);
    return { debut: iso(debut), fin: iso(fin) };
  }
  return { debut: iso(aujourdhui), fin: iso(aujourdhui) };
}

export default function Rapports() {
  const [preset, setPreset] = useState<Preset>('7j');
  const [debut, setDebut] = useState(bornes('7j').debut);
  const [fin, setFin] = useState(bornes('7j').fin);
  const [rapport, setRapport] = useState<Rapport | null>(null);
  const [chargement, setChargement] = useState(true);

  const choisirPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'perso') {
      const b = bornes(p);
      setDebut(b.debut);
      setFin(b.fin);
    }
  };

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const res = await api.get('/ventes/rapport', { params: { debut, fin } });
      setRapport(res.data);
    } catch (e) {
      setRapport(null);
    } finally {
      setChargement(false);
    }
  }, [debut, fin]);

  useEffect(() => { charger(); }, [charger]);

  const libellePeriode = () =>
    debut === fin ? jolieDate(debut) : `du ${jolieDate(debut)} au ${jolieDate(fin)}`;

  const maxJour = Math.max(1, ...(rapport?.parJour.map((j) => j.montant) || [1]));

  // --- Exports ---
  const exportExcel = () => {
    if (!rapport) return;
    const wb = XLSX.utils.book_new();
    const r = rapport.resume;
    const resume = [
      { Indicateur: "Chiffre d'affaires", Valeur: r.chiffreAffaires },
      { Indicateur: 'Nombre de ventes', Valeur: r.nombreVentes },
      { Indicateur: 'Panier moyen', Valeur: r.panierMoyen },
      { Indicateur: 'Remises accordées', Valeur: r.remisesTotales },
      { Indicateur: 'Espèces', Valeur: r.parMode.especes },
      { Indicateur: 'Mobile Money', Valeur: r.parMode.mobileMoney },
      { Indicateur: 'Crédit', Valeur: r.parMode.credit },
      { Indicateur: 'Virement', Valeur: r.parMode.virement },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resume), 'Résumé');
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rapport.topProduits.map((t) => ({ Produit: t.produit, Quantité: t.quantite, Montant: t.montant }))),
      'Top produits',
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rapport.parVendeur.map((v) => ({ Vendeur: v.nom, Ventes: v.nombre, Montant: v.montant }))),
      'Par vendeur',
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rapport.parBoutique.map((b) => ({ Boutique: b.nom, Ventes: b.nombre, Montant: b.montant }))),
      'Par boutique',
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rapport.parJour.map((j) => ({ Jour: j.jour, Ventes: j.nombre, Montant: j.montant }))),
      'Évolution',
    );
    XLSX.writeFile(wb, `rapport-nexacom-${debut}_${fin}.xlsx`);
  };

  const exportPDF = async () => {
    if (!rapport) return;
    const infos = await entrepriseService.getInfos();
    const doc = new jsPDF();
    const r = rapport.resume;
    doc.setFontSize(16);
    doc.text(infos.nom || 'NexaCom', 14, 18);
    doc.setFontSize(11);
    doc.text(`Rapport des ventes — ${libellePeriode()}`, 14, 26);

    autoTable(doc, {
      startY: 32,
      head: [['Indicateur', 'Valeur']],
      body: [
        ["Chiffre d'affaires", `${fmtPdf(r.chiffreAffaires)} FCFA`],
        ['Nombre de ventes', `${r.nombreVentes}`],
        ['Panier moyen', `${fmtPdf(r.panierMoyen)} FCFA`],
        ['Espèces', `${fmtPdf(r.parMode.especes)} FCFA`],
        ['Mobile Money', `${fmtPdf(r.parMode.mobileMoney)} FCFA`],
        ['Crédit', `${fmtPdf(r.parMode.credit)} FCFA`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [24, 95, 165] },
    });

    autoTable(doc, {
      head: [['Top produits', 'Qté', 'Montant']],
      body: rapport.topProduits.map((t) => [t.produit, `${t.quantite}`, `${fmtPdf(t.montant)} FCFA`]),
      theme: 'striped',
      headStyles: { fillColor: [24, 95, 165] },
    });

    autoTable(doc, {
      head: [['Vendeur', 'Ventes', 'Montant']],
      body: rapport.parVendeur.map((v) => [v.nom, `${v.nombre}`, `${fmtPdf(v.montant)} FCFA`]),
      theme: 'striped',
      headStyles: { fillColor: [24, 95, 165] },
    });

    doc.save(`rapport-nexacom-${debut}_${fin}.pdf`);
  };

  const r = rapport?.resume;

  return (
    <div className="rapports-page">
      <div className="page-header">
        <div>
          <h1>Rapports & statistiques</h1>
          <p>Analyse des ventes — {libellePeriode()}</p>
        </div>
        <div className="rap-exports">
          <button className="btn-secondary" onClick={exportExcel} disabled={!rapport}>⬇️ Excel</button>
          <button className="btn-secondary" onClick={exportPDF} disabled={!rapport}>⬇️ PDF</button>
        </div>
      </div>

      {/* SÉLECTEUR DE PÉRIODE */}
      <div className="rap-periode">
        {([
          ['jour', "Aujourd'hui"], ['7j', '7 derniers jours'], ['mois', 'Ce mois'],
          ['moisDernier', 'Mois dernier'], ['perso', 'Personnalisé'],
        ] as [Preset, string][]).map(([p, label]) => (
          <button key={p} className={`rap-preset ${preset === p ? 'active' : ''}`} onClick={() => choisirPreset(p)}>
            {label}
          </button>
        ))}
        {preset === 'perso' && (
          <div className="rap-dates">
            <input type="date" value={debut} max={fin} onChange={(e) => setDebut(e.target.value)} />
            <span>→</span>
            <input type="date" value={fin} min={debut} onChange={(e) => setFin(e.target.value)} />
          </div>
        )}
      </div>

      {chargement ? (
        <div className="page-chargement">Chargement du rapport...</div>
      ) : !r || r.nombreVentes === 0 ? (
        <div className="rap-vide">Aucune vente sur cette période.</div>
      ) : (
        <>
          {/* KPI */}
          <div className="rap-kpi-grid">
            <div className="rap-kpi principal">
              <div className="rap-kpi-lbl">Chiffre d'affaires</div>
              <div className="rap-kpi-val">{fmt(r.chiffreAffaires)} <span>FCFA</span></div>
            </div>
            <div className="rap-kpi">
              <div className="rap-kpi-lbl">Ventes</div>
              <div className="rap-kpi-val">{r.nombreVentes}</div>
            </div>
            <div className="rap-kpi">
              <div className="rap-kpi-lbl">Panier moyen</div>
              <div className="rap-kpi-val">{fmt(r.panierMoyen)} <span>FCFA</span></div>
            </div>
            <div className="rap-kpi">
              <div className="rap-kpi-lbl">Remises accordées</div>
              <div className="rap-kpi-val">{fmt(r.remisesTotales)} <span>FCFA</span></div>
            </div>
          </div>

          <div className="rap-cols">
            {/* Répartition par mode */}
            <div className="rap-card">
              <h3>Encaissements par mode</h3>
              {[
                ['💵 Espèces', r.parMode.especes],
                ['📱 Mobile Money', r.parMode.mobileMoney],
                ['💳 Crédit', r.parMode.credit],
                ['🏦 Virement', r.parMode.virement],
              ].map(([label, val]) => {
                const pct = r.chiffreAffaires ? ((Number(val) / r.chiffreAffaires) * 100) : 0;
                return (
                  <div key={label as string} className="rap-mode">
                    <div className="rap-mode-head"><span>{label}</span><strong>{fmt(Number(val))} F</strong></div>
                    <div className="rap-barre"><div className="rap-barre-fill" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>

            {/* Évolution */}
            <div className="rap-card">
              <h3>Évolution du chiffre d'affaires</h3>
              <div className="rap-evolution">
                {rapport!.parJour.map((j) => (
                  <div key={j.jour} className="rap-evo-col" title={`${jolieDate(j.jour)} : ${fmt(j.montant)} F (${j.nombre} ventes)`}>
                    <div className="rap-evo-bar" style={{ height: `${(j.montant / maxJour) * 100}%` }} />
                    <div className="rap-evo-lbl">{new Date(j.jour).getDate()}/{new Date(j.jour).getMonth() + 1}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top produits */}
          <div className="rap-card">
            <h3>Top produits</h3>
            <table className="rap-table">
              <thead><tr><th>Produit</th><th className="tr">Quantité</th><th className="tr">Montant</th></tr></thead>
              <tbody>
                {rapport!.topProduits.map((t, i) => (
                  <tr key={i}><td>{t.produit}</td><td className="tr">{t.quantite}</td><td className="tr">{fmt(t.montant)} F</td></tr>
                ))}
                {rapport!.topProduits.length === 0 && <tr><td colSpan={3} className="rap-empty">Aucun produit vendu.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="rap-cols">
            {/* Par vendeur */}
            <div className="rap-card">
              <h3>Ventes par vendeur</h3>
              <table className="rap-table">
                <thead><tr><th>Vendeur</th><th className="tr">Ventes</th><th className="tr">Montant</th></tr></thead>
                <tbody>
                  {rapport!.parVendeur.map((v, i) => (
                    <tr key={i}><td>{v.nom}</td><td className="tr">{v.nombre}</td><td className="tr">{fmt(v.montant)} F</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Par boutique */}
            <div className="rap-card">
              <h3>Ventes par boutique</h3>
              <table className="rap-table">
                <thead><tr><th>Boutique</th><th className="tr">Ventes</th><th className="tr">Montant</th></tr></thead>
                <tbody>
                  {rapport!.parBoutique.map((b, i) => (
                    <tr key={i}><td>{b.nom}</td><td className="tr">{b.nombre}</td><td className="tr">{fmt(b.montant)} F</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
